/**
 * The wording of the documents clients are issued, editable without a release.
 *
 * A change applies to the NEXT document produced; a document already issued keeps
 * the text it was produced with. Every change is recorded with the previous value.
 * super_admin only.
 */
import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import adminSettingsService, { type PlatformSetting } from '../../services/adminSettings.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/datetime';
import { colors } from '../../styles/theme';
import {
  MAX_VALUE_LENGTH,
  groupByDocument,
  labelOf,
  saveBlockedReason,
  valueTypeHint,
} from './documentWording.helpers';

export default function DocumentWording(): JSX.Element {
  const [items, setItems] = useState<PlatformSetting[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setItems(await adminSettingsService.list());
    } catch (e: unknown) {
      setItems(null);
      setLoadError(getApiErrorMessage(e, 'Could not load the document wording.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  const onSaved = (saved: PlatformSetting): void => {
    setItems((prev) => (prev ?? []).map((s) => (s.key === saved.key ? saved : s)));
    setFlash(`Saved “${labelOf(saved.key)}”. It applies to the next document produced.`);
  };

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Document wording"
        subtitle="The text that appears in the documents clients are issued. A change applies to the next document produced; documents already issued keep their text."
      />

      {loadError && (
        <div role="alert" className="mb-4 p-3 rounded-lg text-sm"
             style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}>
          <p>{loadError}</p>
          <button type="button" onClick={() => { void reload(); }}
                  className="mt-2 text-sm font-semibold underline">
            Try again
          </button>
        </div>
      )}
      {flash && (
        <output className="block mb-4 p-3 rounded-lg text-sm"
                style={{ background: colors.successBg, border: `1px solid ${colors.successBorder}`, color: colors.success }}>
          {flash}
        </output>
      )}

      {loading && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}

      {!loading && items !== null && items.length === 0 && (
        <p className="text-sm" style={{ color: colors.muted }}>
          There is no editable document wording yet. The list could be read; it is empty.
        </p>
      )}

      {items !== null && groupByDocument(items).map((group) => (
        <section key={group.document} className="rounded-2xl p-5 mb-6"
                 style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
          <h2 className="text-lg font-semibold mb-4" style={{ color: colors.ink }}>
            {group.document}
          </h2>
          {group.items.map((setting) => (
            <SettingEditor key={setting.key} setting={setting} onSaved={onSaved} />
          ))}
        </section>
      ))}
    </div>
  );
}

function SettingEditor({ setting, onSaved }: Readonly<{
  setting: PlatformSetting;
  onSaved: (saved: PlatformSetting) => void;
}>): JSX.Element {
  const [draft, setDraft] = useState(setting.value);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputId = `wording-${setting.key}`;
  const hintId = `${inputId}-hint`;
  const blocked = saveBlockedReason(setting.value, draft, busy);
  const dirty = draft !== setting.value;

  // The stored value is the baseline: after a save (or a reload) the draft starts from it again.
  useEffect(() => { setDraft(setting.value); }, [setting.value]);

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      onSaved(await adminSettingsService.update(setting.key, draft));
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not save this wording.'));
    } finally {
      setBusy(false);
    }
  };

  const fieldStyle = { border: `1px solid ${colors.border}` };

  return (
    <div className="mb-6 last:mb-0">
      <label htmlFor={inputId} className="block text-sm font-semibold mb-1"
             style={{ color: colors.inkSoft }}>
        {labelOf(setting.key)}
      </label>
      {setting.description && (
        <p className="text-xs mb-2" style={{ color: colors.muted }}>{setting.description}</p>
      )}
      {setting.value_type === 'bool' ? (
        <select
          id={inputId}
          aria-describedby={hintId}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={busy}
          className="px-3 py-2 rounded-lg text-sm"
          style={fieldStyle}
        >
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      ) : (
        <textarea
          id={inputId}
          aria-describedby={hintId}
          rows={setting.value_type === 'int' ? 1 : 4}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={busy}
          className="w-full px-3 py-2 rounded-lg text-sm"
          style={fieldStyle}
        />
      )}
      <p id={hintId} className="text-xs mt-1" style={{ color: colors.muted }}>
        {valueTypeHint(setting.value_type)} {draft.length}/{MAX_VALUE_LENGTH} characters.
        Last changed {formatDateTime(setting.updated_at)}.
      </p>

      {error && (
        <p role="alert" className="text-sm mt-2" style={{ color: colors.danger }}>{error}</p>
      )}

      <div className="flex items-center gap-3 mt-2 flex-wrap">
        <button
          type="button"
          disabled={blocked !== null}
          onClick={() => { void save(); }}
          className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
          style={{ background: colors.navy, color: '#FFFFFF' }}
        >
          {busy ? 'Saving…' : 'Save wording'}
        </button>
        {dirty && !busy && (
          <button
            type="button"
            onClick={() => { setDraft(setting.value); setError(null); }}
            className="text-sm font-semibold"
            style={{ color: colors.navy }}
          >
            Discard changes
          </button>
        )}
        {blocked && (
          <span className="text-xs" style={{ color: colors.muted }}>{blocked}</span>
        )}
      </div>
    </div>
  );
}
